/** @jsx jsx */
import { jsx } from "theme-ui"
import PropTypes from "prop-types"
import { Link as GatsbyLink } from "gatsby"

import { useLinkData } from "../hooks/use-link-data"

const default_lang_prefix = process.env.GATSBY_DEFAULT_LANG_PREFIX || null
const default_lang = process.env.GATSBY_DEFAULT_LANG || null
const replace_default_lang_prefix =
  process.env.GATSBY_REPLACE_TO_DEFAULT_LANG_PREFIX || null
const SITE_ROOT = process.env.GATSBY_SITE_ROOT || ""

const removeSiteRoot = (path) => {
  if (path && SITE_ROOT && path.includes(`${SITE_ROOT}/`)) {
    return path.replace(`${SITE_ROOT}/`, "")
  }
  return path
}
const cleanUrl = (url) => {
  // Entfernt doppelte Schrägstriche, außer nach "http:" oder "https:"
  let cleaned = url.replace(/([^:]\/)\/+/g, "$1")
  // Entfernt doppelte Sprach-Präfixe wie /en/en/
  cleaned = cleaned.replace(
    /\/(en|de|fr|es|it|nl|pt|ru|zh|ja|ko)\/\1\//g,
    "/$1/"
  )
  return removeSiteRoot(cleaned)
}

// Funktion zum Entfernen des Sprach-Präfixes
const removeLanguagePrefix = (path) => {
  if (path && replace_default_lang_prefix) {
    // Mit führendem Slash
    const prefixWithSlash = `/${replace_default_lang_prefix}/`
    if (path.startsWith(prefixWithSlash)) {
      return "/" + path.substring(prefixWithSlash.length)
    }

    // Ohne führenden Slash
    const prefixWithoutSlash = `${replace_default_lang_prefix}/`
    if (path.startsWith(prefixWithoutSlash)) {
      return path.substring(prefixWithoutSlash.length)
    }

    // Genau das Präfix mit Slash
    if (path === `/${replace_default_lang_prefix}`) {
      return "/"
    }

    // Genau das Präfix ohne Slash
    if (path === replace_default_lang_prefix) {
      return ""
    }
  }
  return path
}

export const TransformLinkUri = (to, target, language, search = "") => {
  const normalizePath = (path) => path.replace(/\/?(\?|#|$)/, "/$1") // Trailing slash

  let internal = internalCheck(to, target)
  let seoLinks = useLinkData(to, language)

  if (internal) {
    // Zuerst home-Pfade behandeln
    to =
      to === "/home" || to === "home"
        ? "/"
        : to && to.startsWith("/")
          ? `${to}`
          : `/${to}`

    // Sprach-spezifische Home-Behandlung
    to =
      to === "/" + language + "/home" || to === language + "/home"
        ? "/" + language + "/"
        : to

    // SEO Links anwenden falls vorhanden
    if (seoLinks[to.substr(1)]) {
      const seoLink = seoLinks[to.substr(1)]
      to = seoLink.startsWith('/') ? seoLink : `/${seoLink}`
      to = cleanUrl(to)
    }

    // NEUE LOGIK: Nur Sprachpräfix hinzufügen wenn wirklich benötigt

    // Prüfe ob wir überhaupt ein Präfix hinzufügen sollen
    const shouldAddPrefix =
      default_lang_prefix !== null && default_lang_prefix !== ""

    // Für nicht-default Sprachen
    if (
      language &&
      language !== "" &&
      language !== "default" &&
      language !== default_lang
    ) {
      // Nur hinzufügen wenn der Pfad noch nicht mit der Sprache beginnt
      if (!to.startsWith(`/${language}/`) && !to.startsWith(`${language}/`)) {
        if (to === "/") {
          to = `/${language}/`
        } else {
          const pathWithoutSlash = to.startsWith("/") ? to.substr(1) : to
          to = `/${language}/${pathWithoutSlash}`
        }
      }
    }
    // Für default Sprache - nur wenn default_lang_prefix gesetzt ist
    else if (
      (language === "default" || language === default_lang) &&
      shouldAddPrefix
    ) {
      // Nur hinzufügen wenn der Pfad noch nicht mit dem Präfix beginnt
      if (
        !to.startsWith(`/${default_lang_prefix}/`) &&
        !to.startsWith(`${default_lang_prefix}/`)
      ) {
        if (to === "/") {
          to = `/${default_lang_prefix}/`
        } else {
          const pathWithoutSlash = to.startsWith("/") ? to.substr(1) : to
          to = `/${default_lang_prefix}/${pathWithoutSlash}`
        }
      }
    } else {
      if (to !== "/") {
        to = to.startsWith("/") ? to : `/${to}`
      }
    }

    // Wenn kein Präfix gesetzt ist, füge auch keins hinzu
    // Der Pfad bleibt wie er ist

    to = cleanUrl(to)
    to = normalizePath(to)

    if (search) {
      to += `?${search}`
    }
  }

  // Entferne Sprach-Präfix wenn konfiguriert
  to = removeLanguagePrefix(to)
  return to
}

let internalCheck = (to, target) => {
  let internal = !/(http)/.test(to)

  to = typeof to === "string" ? to : ""
  internal =
    internal &&
    !target &&
    !to.startsWith("mailto:") &&
    !to.startsWith("tel:") &&
    !to.startsWith("#")

  return internal
}

// Since DOM elements <a> cannot receive activeClassName
// and partiallyActive, destructure the prop here and
// pass it only to GatsbyLink
const Link = ({
  children,
  to,
  activeClassName,
  partiallyActive,
  target,
  search,
  language,
  ...other
}) => {
  // Tailor the following test to your environment.
  // This example assumes that any internal link (intended for Gatsby)
  // will start with exactly one slash, and that anything else is external.
  let internal = internalCheck(to, target)

  let internalLink = TransformLinkUri(to, target, language, search)

  // Use Gatsby Link for internal links, and <a> for others
  if (internal) {
    return (
      <GatsbyLink
        to={internalLink}
        activeClassName={activeClassName}
        partiallyActive={partiallyActive}
        {...other}
      >
        {children}
      </GatsbyLink>
    )
  }
  return (
    <a href={to} {...(target ? { target: target } : {})} {...other}>
      {children}
    </a>
  )
}

Link.propTypes = {
  children: PropTypes.node.isRequired,
  to: PropTypes.string.isRequired,
  activeClassName: PropTypes.string,
  partiallyActive: PropTypes.string,
  target: PropTypes.string,
  search: PropTypes.string,
  language: PropTypes.string,
  other: PropTypes.object,
}

export default Link
